
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { TagIcon } from "lucide-react";

interface DiscountFormProps {
  onApplyAffiliate: (code: string) => void;
  onApplyCoupon: (code: string) => void;
  className?: string;
}

const DiscountForm = ({ onApplyAffiliate, onApplyCoupon, className = "" }: DiscountFormProps) => {
  const [discountCode, setDiscountCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const validateDiscountCode = async (code: string) => {
    // First check if it's an affiliate code
    const { data: affiliateData, error: affiliateError } = await supabase
      .from("affiliates")
      .select("*")
      .eq("code", code)
      .eq("application_status", "approved")
      .single();

    if (!affiliateError && affiliateData) {
      return { type: "affiliate", data: affiliateData };
    }

    // Check if it's a coupon code
    const { data: couponData, error: couponError } = await supabase
      .from("coupons")
      .select("*")
      .eq("code", code)
      .eq("active", true)
      .single();

    if (!couponError && couponData) {
      // Check if coupon is expired
      if (couponData.expire_date && new Date(couponData.expire_date) < new Date()) {
        return { type: "error", message: "This coupon has expired" };
      }

      // Check if coupon has reached its maximum uses
      if (couponData.max_uses && couponData.current_uses >= couponData.max_uses) {
        return { type: "error", message: "This coupon has reached its maximum uses" };
      }

      return { type: "coupon", data: couponData };
    }

    return { type: "error", message: "Invalid discount code" };
  };

  const handleApplyCode = async () => {
    if (!discountCode.trim()) return;
    
    setIsLoading(true);
    
    try {
      const result = await validateDiscountCode(discountCode);
      
      if (result.type === "affiliate") {
        onApplyAffiliate(discountCode);
        toast.success("Affiliate code applied successfully");
      } else if (result.type === "coupon") {
        onApplyCoupon(discountCode);
        toast.success("Coupon code applied successfully");
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error("Error validating discount code:", error);
      toast.error("Failed to validate discount code");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <Label htmlFor="discount-code">Discount Code</Label>
      <div className="flex gap-2">
        <div className="relative flex-grow">
          <Input
            id="discount-code"
            placeholder="Enter affiliate or coupon code"
            value={discountCode}
            onChange={(e) => setDiscountCode(e.target.value)}
            className="bg-gaming-dark border-gaming-light-gray/40 pl-9"
          />
          <TagIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
        </div>
        <Button 
          onClick={handleApplyCode}
          disabled={isLoading || !discountCode.trim()}
          variant="secondary"
        >
          Apply
        </Button>
      </div>
    </div>
  );
};

export default DiscountForm;
